import React, { useState, useEffect } from 'react';
import AccessibleModal from './AccessibleModal';
import { Search, Star, ShoppingCart, Check, X, Filter, ChevronDown, User } from 'lucide-react';
import { hiringService } from '../lib/services';
import { useWorkspace } from '../contexts/WorkspaceContext';
import type { MarketplaceReplica } from '../types/api';
import OnboardingWizard from './OnboardingWizard';
import { agents as localAgents } from '../data/agentsData';

interface HiringFlowViewProps {
    onNavigate: (view: string) => void;
}

export default function HiringFlowView({ onNavigate }: HiringFlowViewProps) {
    const { currentWorkspace, teams } = useWorkspace();
    const [replicas, setReplicas] = useState<MarketplaceReplica[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('All');
    const [cart, setCart] = useState<Array<{
        replica: MarketplaceReplica;
        teamId?: string;
        role?: string;
    }>>([]);
    const [showCart, setShowCart] = useState(false);
    const [isCheckingOut, setIsCheckingOut] = useState(false);

    // Onboarding State
    const [showOnboarding, setShowOnboarding] = useState(false);
    const [onboardingContractId, setOnboardingContractId] = useState<string | null>(null);
    const [onboardingReplica, setOnboardingReplica] = useState<MarketplaceReplica | null>(null);

    const categories = ['All', 'HR', 'Marketing', 'Finance', 'Customer Service'];

    // Convert local agents data to MarketplaceReplica format for fallback
    const getLocalReplicas = (): MarketplaceReplica[] => {
        return localAgents.map(agent => ({
            id: agent.id,
            name: agent.name,
            category: agent.category,
            description: agent.description,
            skills: agent.skills,
            rating: agent.rating,
            reviews: agent.reviews,
            hasHumanGuardian: agent.hasHumanGuardian,
            price: agent.price,
        }));
    };

    useEffect(() => {
        loadReplicas();
    }, [selectedCategory, searchQuery]);

    const loadReplicas = async () => {
        setIsLoading(true);
        try {
            const response = await hiringService.browseMarketplace({
                category: selectedCategory !== 'All' ? selectedCategory : undefined,
                search: searchQuery || undefined,
            });
            console.log('[HiringFlow] API response:', response);
            if (response.data && response.data.replicas && response.data.replicas.length > 0) {
                setReplicas(response.data.replicas);
            } else {
                // Fallback to local data when backend returns empty
                console.log('[HiringFlow] Using local fallback data');
                let fallback = getLocalReplicas();
                if (selectedCategory !== 'All') {
                    fallback = fallback.filter(r => r.category === selectedCategory);
                }
                if (searchQuery) {
                    fallback = fallback.filter(r =>
                        r.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                        (r.description ?? '').toLowerCase().includes(searchQuery.toLowerCase())
                    );
                }
                setReplicas(fallback);
            }
        } catch (err) {
            // On error, use local data
            console.error('[HiringFlow] API error, using fallback:', err);
            let fallback = getLocalReplicas();
            if (selectedCategory !== 'All') {
                fallback = fallback.filter(r => r.category === selectedCategory);
            }
            if (searchQuery) {
                fallback = fallback.filter(r =>
                    r.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    (r.description ?? '').toLowerCase().includes(searchQuery.toLowerCase())
                );
            }
            setReplicas(fallback);
        }
        setIsLoading(false);
    };

    const addToCart = (replica: MarketplaceReplica) => {
        if (cart.find(item => item.replica.id === replica.id)) return;
        setCart([...cart, { replica }]);
    };

    const removeFromCart = (replicaId: string) => {
        setCart(cart.filter(item => item.replica.id !== replicaId));
    };

    const updateCartItem = (replicaId: string, updates: { teamId?: string; role?: string }) => {
        setCart(cart.map(item =>
            item.replica.id === replicaId
                ? { ...item, ...updates }
                : item
        ));
    };

    const handleCheckout = async () => {
        if (!currentWorkspace || cart.length === 0) return;

        setIsCheckingOut(true);

        try {
            const response = await hiringService.initiateHiring({
                workspaceId: currentWorkspace.id,
                replicas: cart.map(item => ({
                    replicaId: item.replica.id,
                    teamId: item.teamId,
                    role: item.role,
                })),
            });

            if (response.data?.contract) {
                // For MVP Demo: Skip payment redirect and go straight to Onboarding
                setOnboardingContractId(response.data.contract.id);
                setOnboardingReplica(cart[0].replica);
                setShowCart(false);
                setCart([]);
                setIsCheckingOut(false);
                setShowOnboarding(true);
                return;
            }
        } catch {
            // Backend call failed — fall through to client-side mock
        }

        // MVP Fallback: generate a mock contract client-side so onboarding always works
        const mockContractId = crypto.randomUUID();
        setOnboardingContractId(mockContractId);
        setOnboardingReplica(cart[0].replica);
        setShowCart(false);
        setCart([]);
        setIsCheckingOut(false);
        setShowOnboarding(true);
    };

    const totalPrice = cart.reduce((sum, item) =>
        sum + item.replica.price.amount, 0
    );

    const handleOnboardingComplete = () => {
        setShowOnboarding(false);
        onNavigate('dashboard');
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-hig-text-primary">AI Marketplace</h1>
                    <p className="text-hig-text-secondary mt-1">
                        Browse and hire AI replicas for your team.
                    </p>
                </div>
                <button
                    onClick={() => setShowCart(true)}
                    className="relative flex items-center gap-2 px-4 py-2 bg-hig-accent hover:opacity-90 text-white rounded-lg transition-colors"
                >
                    <ShoppingCart size={18} />
                    Cart
                    {cart.length > 0 && (
                        <span className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                            {cart.length}
                        </span>
                    )}
                </button>
            </div>

            {/* Filters */}
            <div className="bg-hig-bg-elevated rounded-xl p-4 border border-hig-border">
                <div className="flex flex-col sm:flex-row gap-4">
                    {/* Search */}
                    <div className="relative flex-1">
                        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-hig-text-tertiary" />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search agents..."
                            className="w-full pl-10 pr-4 py-2 bg-hig-bg-tertiary border border-hig-border rounded-lg text-hig-text-primary placeholder-hig-text-tertiary focus:outline-none focus:border-hig-accent"
                        />
                    </div>

                    {/* Category Filter */}
                    <div className="flex gap-2 overflow-x-auto pb-2 sm:pb-0">
                        {categories.map((category) => (
                            <button
                                key={category}
                                onClick={() => setSelectedCategory(category)}
                                className={`px-4 py-2 rounded-lg whitespace-nowrap transition-colors ${selectedCategory === category
                                    ? 'bg-hig-accent text-white'
                                    : 'bg-hig-bg-tertiary text-hig-text-secondary hover:bg-hig-bg-secondary'
                                    }`}
                                aria-pressed={selectedCategory === category} // Added aria-pressed
                            >
                                {category}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Replicas Grid */}
            {isLoading ? (
                <div className="flex items-center justify-center h-64">
                    <div className="w-8 h-8 border-2 border-hig-accent border-t-transparent rounded-full animate-spin" />
                </div>
            ) : replicas.length === 0 ? (
                <div className="bg-hig-bg-elevated rounded-xl border border-hig-border p-12 text-center">
                    <Search size={48} className="mx-auto text-hig-text-tertiary mb-4" />
                    <h3 className="text-lg font-medium text-hig-text-primary mb-2">No agents found</h3>
                    <p className="text-hig-text-secondary">Try adjusting your search or filters.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {replicas.map((replica) => {
                        const isInCart = cart.some(item => item.replica.id === replica.id);

                        return (
                            <div
                                key={replica.id}
                                className="bg-hig-bg-elevated rounded-xl border border-hig-border overflow-hidden hover:border-hig-accent/50 transition-colors"
                            >
                                {/* Image placeholder */}
                                <div className="h-40 bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center">
                                    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                                        <span className="text-2xl font-bold text-white">
                                            {replica.name.charAt(0)}
                                        </span>
                                    </div>
                                </div>

                                <div className="p-5">
                                    <div className="flex items-start justify-between mb-2">
                                        <div>
                                            <h3 className="text-lg font-semibold text-hig-text-primary">{replica.name}</h3>
                                            <p className="text-sm text-hig-text-secondary">{replica.category}</p>
                                        </div>
                                        <div className="flex items-center gap-1 text-yellow-400">
                                            <Star size={14} className="fill-current" />
                                            <span className="text-sm">{replica.rating}</span>
                                        </div>
                                    </div>

                                    {/* Skills */}
                                    <div className="flex flex-wrap gap-2 mb-4">
                                        {(replica.skills || []).slice(0, 3).map((skill) => (
                                            <span
                                                key={skill}
                                                className="px-2 py-1 bg-hig-bg-tertiary text-xs text-hig-text-secondary rounded"
                                            >
                                                {skill}
                                            </span>
                                        ))}
                                    </div>

                                    <div className="flex items-center justify-between pt-4 border-t border-hig-border">
                                        <div>
                                            <span className="text-xl font-bold text-hig-text-primary">
                                                ${replica.price?.amount ?? 0}
                                            </span>
                                            <span className="text-sm text-hig-text-secondary">
                                                /{replica.price?.period ?? 'hour'}
                                            </span>
                                        </div>
                                        <button
                                            onClick={() => isInCart ? removeFromCart(replica.id) : addToCart(replica)}
                                            className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${isInCart
                                                ? 'bg-green-500/20 text-green-500 border border-green-500/50'
                                                : 'bg-hig-accent hover:opacity-90 text-white'
                                                }`}
                                        >
                                            {isInCart ? (
                                                <>
                                                    <Check size={16} />
                                                    Added
                                                </>
                                            ) : (
                                                <>
                                                    <ShoppingCart size={16} />
                                                    Hire
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Cart Sidebar */}
            {showCart && (
                <AccessibleModal
                    open={showCart}
                    onClose={() => setShowCart(false)}
                    title="Hiring Cart"
                    backdropClassName="!justify-end"
                    panelClassName="w-full max-w-md bg-hig-bg-elevated h-full overflow-y-auto">
                    <div className="p-6">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-xl font-semibold text-hig-text-primary">Hiring Cart</h2>
                            <button
                                onClick={() => setShowCart(false)}
                                className="p-2 text-hig-text-tertiary hover:text-hig-text-primary"
                                aria-label="Close cart"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        {cart.length === 0 ? (
                            <div className="text-center py-12">
                                <ShoppingCart size={48} className="mx-auto text-hig-text-tertiary mb-4" />
                                <p className="text-hig-text-secondary">Your cart is empty</p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {cart.map((item) => (
                                    <div key={item.replica.id} className="bg-hig-bg-tertiary rounded-xl p-4">
                                        <div className="flex items-start justify-between mb-3">
                                            <div>
                                                <h3 className="font-medium text-hig-text-primary">{item.replica.name}</h3>
                                                <p className="text-sm text-hig-text-secondary">{item.replica.category}</p>
                                            </div>
                                            <button
                                                onClick={() => removeFromCart(item.replica.id)}
                                                className="text-hig-text-tertiary hover:text-red-400"
                                                aria-label={`Remove ${item.replica.name} from cart`}
                                            >
                                                <X size={16} />
                                            </button>
                                        </div>

                                        {/* Team Assignment */}
                                        <div className="space-y-2">
                                            <label className="text-xs text-hig-text-secondary">Assign to Team (optional)</label>
                                            <select
                                                value={item.teamId || ''}
                                                onChange={(e) => updateCartItem(item.replica.id, { teamId: e.target.value || undefined })}
                                                className="w-full px-3 py-2 bg-hig-bg-secondary border border-hig-border rounded-lg text-hig-text-primary text-sm focus:outline-none focus:border-hig-accent"
                                            >
                                                <option value="">No team</option>
                                                {teams.map((team) => (
                                                    <option key={team.id} value={team.id}>{team.name}</option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="mt-3 flex justify-between text-sm">
                                            <span className="text-hig-text-secondary">Price</span>
                                            <span className="text-hig-text-primary font-medium">
                                                ${item.replica.price?.amount ?? 0}/{item.replica.price?.period ?? 'hour'}
                                            </span>
                                        </div>
                                    </div>
                                ))}

                                {/* Total */}
                                <div className="border-t border-hig-border pt-4">
                                    <div className="flex justify-between mb-4">
                                        <span className="text-hig-text-secondary">Total (first period)</span>
                                        <span className="text-xl font-bold text-hig-text-primary">${totalPrice.toFixed(2)}</span>
                                    </div>
                                    <button
                                        onClick={handleCheckout}
                                        disabled={isCheckingOut}
                                        className="w-full py-3 bg-hig-accent hover:opacity-90 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                                    >
                                        {isCheckingOut ? 'Processing...' : 'Hire & Start Onboarding'}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </AccessibleModal>
            )}

            {/* Onboarding Wizard Modal */}
            {showOnboarding && onboardingContractId && onboardingReplica && (
                <OnboardingWizard
                    contractId={onboardingContractId}
                    replica={onboardingReplica}
                    onComplete={handleOnboardingComplete}
                />
            )}
        </div>
    );
}
